import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel, Typography, Box, CircularProgress, Alert, Chip,
  FormControlLabel, Checkbox, Snackbar,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import StorefrontIcon from '@mui/icons-material/Storefront';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

const EzCaterARDetailsDialog = ({ open, onClose, businessDate, storeId, firebaseUser }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [details, setDetails] = useState([]);
  const [showOrderIds, setShowOrderIds] = useState(false);
  const [orderBy, setOrderBy] = useState('totalAmount');
  const [order, setOrder] = useState('desc');
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);

  const handleRequestSort = (property) => {
    setOrder(orderBy === property && order === 'asc' ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedDetails = useMemo(() => {
    const amountFields = ['salesAmount', 'serviceChargeAmount', 'feeItemAmount', 'taxAmount', 'tipAmount', 'paymentAmount', 'totalAmount'];
    return [...details].sort((a, b) => {
      let aVal = amountFields.includes(orderBy) ? (parseFloat(a[orderBy]) || 0) : String(a[orderBy] || '').toLowerCase();
      let bVal = amountFields.includes(orderBy) ? (parseFloat(b[orderBy]) || 0) : String(b[orderBy] || '').toLowerCase();
      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [details, orderBy, order]);

  const totals = useMemo(() => details.reduce((acc, d) => ({
    sales: acc.sales + (parseFloat(d.salesAmount) || 0),
    serviceCharge: acc.serviceCharge + (parseFloat(d.serviceChargeAmount) || 0),
    feeItem: acc.feeItem + (parseFloat(d.feeItemAmount) || 0),
    tax: acc.tax + (parseFloat(d.taxAmount) || 0),
    tip: acc.tip + (parseFloat(d.tipAmount) || 0),
    payment: acc.payment + (parseFloat(d.paymentAmount) || 0),
    total: acc.total + (parseFloat(d.totalAmount) || 0),
  }), { sales: 0, serviceCharge: 0, feeItem: 0, tax: 0, tip: 0, payment: 0, total: 0 }), [details]);

  const orderTypeCounts = useMemo(() => details.reduce(
    (acc, d) => { if (d.orderType === 'Delivery') acc.delivery++; else if (d.orderType === 'Pickup') acc.pickup++; return acc; },
    { delivery: 0, pickup: 0 }
  ), [details]);

  useEffect(() => {
    if (open && businessDate && storeId) fetchDetails();
  }, [open, businessDate, storeId]);

  const fetchDetails = async () => {
    if (!firebaseUser) return;
    try {
      setLoading(true);
      setError(null);
      const token = await firebaseUser.getIdToken(true);
      const { data } = await axios.get('/hd-api/gl-export/ezcater-ar-details', {
        params: { businessDate, storeId },
        headers: { Authorization: `Bearer ${token}` },
      });
      setDetails(data.data || []);
    } catch (err) {
      console.error('Error fetching ezCater AR details:', err);
      setError('Failed to load ezCater AR details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fmt = (val) => {
    const n = parseFloat(val);
    if (isNaN(n)) return '-';
    return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleExport = () => {
    const exportData = sortedDetails.map((d) => {
      const row = {
        'Order Type': d.orderType,
        'Delivery Partner': d.deliveryPartner || '',
        'State': d.state,
        'Sales': parseFloat(d.salesAmount) || 0,
        'Service Charges': parseFloat(d.serviceChargeAmount) || 0,
        'Fee Items': parseFloat(d.feeItemAmount) || 0,
        'Tax': parseFloat(d.taxAmount) || 0,
        'Tip': parseFloat(d.tipAmount) || 0,
        'Payment': parseFloat(d.paymentAmount) || 0,
        'Total': parseFloat(d.totalAmount) || 0,
      };
      if (showOrderIds) {
        row['Order GUID'] = d.orderGuid || '';
        row['Olo ID'] = d.oloRef || '';
        row['ezCater ID'] = d.externalReference || '';
      }
      return row;
    });
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'ezCater AR');
    XLSX.writeFile(wb, `ezcater-ar-${storeId}-${businessDate}.xlsx`);
  };

  const SortCell = ({ field, label, align = 'right' }) => (
    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }} align={align}>
      <TableSortLabel
        active={orderBy === field}
        direction={orderBy === field ? order : 'asc'}
        onClick={() => handleRequestSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth PaperProps={{ sx: { maxHeight: '80vh' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AccountBalanceIcon color="primary" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6">ezCater AR Details</Typography>
          <Typography variant="body2" color="text.secondary">
            122300 — Store {storeId} — {businessDate}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip icon={<LocalShippingIcon sx={{ fontSize: 16 }} />} label={`${orderTypeCounts.delivery} Delivery`} size="small" color="info" variant="outlined" />
          <Chip icon={<StorefrontIcon sx={{ fontSize: 16 }} />} label={`${orderTypeCounts.pickup} Pickup`} size="small" color="secondary" variant="outlined" />
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!loading && !error && details.length === 0 && <Alert severity="info">No ezCater AR details found for this date.</Alert>}

        {!loading && !error && details.length > 0 && (
          <>
            <TableContainer sx={{ maxHeight: 420, userSelect: 'text' }} onCopy={() => setCopySnackbarOpen(true)}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {showOrderIds && (
                      <>
                        <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Order GUID</TableCell>
                        <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Olo ID</TableCell>
                        <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>ezCater ID</TableCell>
                      </>
                    )}
                    {storeId === 'ALL' && <SortCell field="storeId" label="Store" align="left" />}
                    <SortCell field="orderType" label="Type" align="left" />
                    <SortCell field="deliveryPartner" label="Delivery Partner" align="left" />
                    <SortCell field="state" label="State" align="left" />
                    <SortCell field="salesAmount" label="Sales" />
                    <SortCell field="serviceChargeAmount" label="Svc Charges" />
                    <SortCell field="feeItemAmount" label="Fee Items" />
                    <SortCell field="taxAmount" label="Tax" />
                    <SortCell field="tipAmount" label="Tip" />
                    <SortCell field="paymentAmount" label="Payment" />
                    <SortCell field="totalAmount" label="Total" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedDetails.map((d, i) => (
                    <TableRow key={`${d.orderGuid}-${i}`} sx={{ backgroundColor: i % 2 === 0 ? '#fff' : '#fafafa', '&:hover': { backgroundColor: '#f5f5f5' } }}>
                      {showOrderIds && (
                        <>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{d.orderGuid || '-'}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{d.oloRef || '-'}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{d.externalReference || '-'}</TableCell>
                        </>
                      )}
                      {storeId === 'ALL' && <TableCell>{d.storeId}</TableCell>}
                      <TableCell>
                        <Chip
                          icon={d.orderType === 'Delivery' ? <LocalShippingIcon sx={{ fontSize: 14 }} /> : <StorefrontIcon sx={{ fontSize: 14 }} />}
                          label={d.orderType || 'Unknown'}
                          size="small"
                          color={d.orderType === 'Delivery' ? 'info' : 'secondary'}
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      </TableCell>
                      <TableCell>{d.deliveryPartner || '-'}</TableCell>
                      <TableCell>{d.state || '-'}</TableCell>
                      <TableCell align="right">{fmt(d.salesAmount)}</TableCell>
                      <TableCell align="right">{fmt(d.serviceChargeAmount)}</TableCell>
                      <TableCell align="right">{fmt(d.feeItemAmount)}</TableCell>
                      <TableCell align="right">{fmt(d.taxAmount)}</TableCell>
                      <TableCell align="right" sx={{ color: parseFloat(d.tipAmount) > 0 ? 'success.main' : 'text.secondary' }}>{fmt(d.tipAmount)}</TableCell>
                      <TableCell align="right">{fmt(d.paymentAmount)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 500 }}>{fmt(d.totalAmount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">{details.length} order{details.length !== 1 ? 's' : ''}</Typography>
                <FormControlLabel
                  control={<Checkbox size="small" checked={showOrderIds} onChange={(e) => setShowOrderIds(e.target.checked)} />}
                  label={<Typography variant="body2">Show Order IDs</Typography>}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2.5 }}>
                {[
                  { label: 'Sales', value: totals.sales },
                  { label: 'Svc Charges', value: totals.serviceCharge },
                  { label: 'Fee Items', value: totals.feeItem },
                  { label: 'Tax', value: totals.tax },
                  { label: 'Tip', value: totals.tip },
                  { label: 'Payment', value: totals.payment },
                  { label: 'Total', value: totals.total, bold: true },
                ].map(({ label, value, bold }) => (
                  <Box key={label} sx={{ textAlign: 'right' }}>
                    <Typography variant="body2" color="text.secondary">{label}</Typography>
                    <Typography variant={bold ? 'h6' : 'body1'} sx={{ fontWeight: bold ? 600 : 500 }}>
                      {fmt(value)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleExport} disabled={details.length === 0}>
          Export
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      <Snackbar open={copySnackbarOpen} autoHideDuration={2000} onClose={() => setCopySnackbarOpen(false)}
        message="Copied to clipboard" anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Dialog>
  );
};

export default EzCaterARDetailsDialog;
